import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

interface BunsenBurnerProps {
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: number;
  flameState?: 'off' | 'safety' | 'heating';
  intensity?: number; // 0.0 to 1.0 multiplier for flame size and heat
  onToggle?: () => void; // Called when the gas valve is clicked
  onAdjust?: () => void; // Called when the collar is clicked to cycle flame intensity
}

export function BunsenBurner({ 
  position = [0, 0, 0], 
  rotation = [0, 0, 0], 
  scale = 1, 
  flameState = 'off',
  intensity = 1.0,
  onToggle,
  onAdjust
}: BunsenBurnerProps) {
  const groupRef = useRef<THREE.Group>(null);
  const lightRef = useRef<THREE.PointLight>(null);
  
  // Flame layers
  const innerConeRef = useRef<THREE.Mesh>(null);
  const outerConeRef = useRef<THREE.Mesh>(null);

  // Randomize start phase for flickering
  const phase = useMemo(() => Math.random() * Math.PI, []);

  useFrame((state) => {
    if (flameState !== 'off') {
      const time = state.clock.elapsedTime * 10;
      // High-frequency jitter for realistic roaring/flickering
      const flicker = Math.sin(time + phase) * 0.05 + Math.sin(time * 2.3 + phase) * 0.05;
      
      if (lightRef.current) {
        const baseIntensity = flameState === 'heating' ? 0.8 : 0.4;
        lightRef.current.intensity = Math.max(0.1, (baseIntensity + flicker * 0.3) * intensity);
      }

      if (innerConeRef.current && outerConeRef.current) {
        // Multiplier based on user passed intensity
        const scaleBaseY = intensity;
        const scaleBaseX = 0.5 + (intensity * 0.5);

        // Flame dances slightly in scale and squashes up & down
        const scaleY = scaleBaseY + flicker * (flameState === 'safety' ? 0.3 : 0.05);
        const scaleX = scaleBaseX + flicker * (flameState === 'safety' ? 0.1 : 0.02);

        innerConeRef.current.scale.set(scaleX, scaleY, scaleX);
        outerConeRef.current.scale.set(scaleX, scaleY + 0.02, scaleX);
      }
    }
  });

  // Smooth flexible path for the rubber gas tube
  const tubePath = useMemo(() => {
    return new THREE.CatmullRomCurve3([
      new THREE.Vector3(0, 0, 0),             // Connects flush over the brass barb (World ~[0.048, 0.035, 0])
      new THREE.Vector3(0.015, 0, 0),         // Stays straight briefly
      new THREE.Vector3(0.04, -0.015, 0.02),  // Droops smoothly downward & outward
      new THREE.Vector3(0.04, -0.031, 0.08),  // Lands flat onto the table (Y = -0.031)
      new THREE.Vector3(0.02, -0.031, 0.15),  // Snakes away off-screen along the desk
    ], false, 'catmullrom', 0.6);
  }, []);

  return (
    <group ref={groupRef} position={position} rotation={rotation} scale={scale}>
      
      {/* --- HARDWARE --- */}
      
      {/* 1. Heavy Cast Iron Base */}
      {/* Bottom pad */}
      <mesh position={[0, 0.005, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[0.04, 0.042, 0.01, 32]} />
        <meshStandardMaterial color="#11151c" roughness={0.8} metalness={0.6} />
      </mesh>
      
      {/* Sloped mid section */}
      <mesh position={[0, 0.02, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[0.015, 0.04, 0.02, 32]} />
        <meshStandardMaterial color="#1c2331" roughness={0.7} metalness={0.5} />
      </mesh>
      
      {/* Base collar */}
      <mesh position={[0, 0.035, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[0.012, 0.015, 0.01, 16]} />
        <meshStandardMaterial color="#11151c" roughness={0.6} metalness={0.7} />
      </mesh>

      {/* 2. Gas Inlet Pipe & Valve Assembly */}
      <group position={[0.015, 0.035, 0]} rotation={[0, 0, -Math.PI / 2]}>
        {/* Main stem extending outwards */}
        <mesh position={[0, 0.018, 0]} castShadow>
          <cylinderGeometry args={[0.004, 0.004, 0.035, 16]} />
          <meshStandardMaterial color="#d4b46a" roughness={0.3} metalness={0.8} /> {/* Brass */}
        </mesh>
        
        {/* Gas Knob / Valve Mechanism attached to the side of the stem */}
        <group 
          position={[0, 0.01, 0.005]} 
          rotation={[Math.PI / 2, 0, 0]}
          onClick={(e) => {
            e.stopPropagation();
            if (onToggle) onToggle();
          }}
          onPointerOver={() => document.body.style.cursor = 'pointer'}
          onPointerOut={() => document.body.style.cursor = 'auto'}
        >
          <mesh position={[0, 0.003, 0]} castShadow>
            <cylinderGeometry args={[0.002, 0.002, 0.01, 16]} />
            <meshStandardMaterial color="#d4b46a" roughness={0.3} metalness={0.8} />
          </mesh>
          <mesh position={[0, 0.008, 0]} castShadow>
            {/* The actual grippy knob turned out */}
            <cylinderGeometry args={[0.005, 0.005, 0.002, 16]} />
            <meshStandardMaterial color={flameState !== 'off' ? "#d32f2f" : "#111"} roughness={0.8} />
          </mesh>
        </group>

        {/* Hose barbs (ribs for grip) at the end of the stem */}
        {[0.025, 0.030, 0.035].map((y, i) => (
          <mesh key={i} position={[0, y, 0]} castShadow>
            <torusGeometry args={[0.0042, 0.001, 8, 16]} />
            <meshStandardMaterial color="#c0a05a" roughness={0.4} metalness={0.8} />
          </mesh>
        ))}
      </group>

      {/* 3. Rubber Gas Tube - Now a perfectly fit parametric sweep */}
      <mesh position={[0.048, 0.035, 0]} castShadow receiveShadow>
        <tubeGeometry args={[tubePath, 64, 0.0045, 16, false]} />
        <meshStandardMaterial color="#8b1e1e" roughness={0.8} /> {/* Thick dark red rubber */}
      </mesh>

      {/* 4. Air Intake Collar (rotating sleeve) to control intensity */}
      <group 
        position={[0, 0.045, 0]} 
        rotation={[0, (1 - intensity) * Math.PI, 0]}
        onPointerOver={(e) => { e.stopPropagation(); document.body.style.cursor = 'pointer'; }}
        onPointerOut={(e) => { e.stopPropagation(); document.body.style.cursor = 'auto'; }}
        onClick={(e) => {
          e.stopPropagation();
          if (onAdjust) onAdjust(); 
        }}
      >
        <mesh castShadow>
          <cylinderGeometry args={[0.0075, 0.0075, 0.012, 24]} />
          <meshStandardMaterial color="#a0a0a0" roughness={0.5} metalness={0.7} /> {/* Aluminum/Steel */}
        </mesh>
        
        {/* Intake Hole Detail (dark indent) that rotates with collar */}
        <mesh position={[0, 0, 0.007]} rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[0.0025, 0.0025, 0.002, 12]} />
          <meshStandardMaterial color="#050505" roughness={1} />
        </mesh>
      </group>

      {/* 4. Main Chimney / Barrel */}
      {/* The long vertical tube */}
      <mesh position={[0, 0.105, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[0.006, 0.006, 0.11, 24]} />
        <meshStandardMaterial color="#cccccc" roughness={0.3} metalness={0.85} /> {/* Polished Steel */}
      </mesh>
      
      {/* 5. Top Rim (slightly flared or thick) */}
      <mesh position={[0, 0.16, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[0.007, 0.006, 0.004, 24]} />
        <meshStandardMaterial color="#aaaaaa" roughness={0.4} metalness={0.7} /> 
      </mesh>


      {/* --- FLAME --- */}
      {flameState !== 'off' && (
        <group position={[0, 0.162, 0]}>
          
          {/* Flame Base glow - seals the flame onto the rim */}
          <mesh position={[0, 0.002, 0]}>
            <cylinderGeometry args={[0.004, 0.005, 0.004, 16]} />
            <meshBasicMaterial color={flameState === 'heating' ? "#00ffff" : "#ffffff"} transparent opacity={0.6} blending={THREE.AdditiveBlending} />
          </mesh>

          {/* Intense Core ( Base of the flame ) */}
          <mesh position={[0, 0.015, 0]}>
            <coneGeometry args={[0.003, 0.03, 16]} />
            <meshBasicMaterial 
              color="#dffff5" 
              transparent 
              opacity={0.9} 
              blending={THREE.AdditiveBlending}
              depthWrite={false}
            />
          </mesh>

          {/* Inner Cone ( hottest blue part ) */}
          <mesh ref={innerConeRef} position={[0, flameState === 'heating' ? 0.02 : 0.03, 0]}>
            <coneGeometry args={[flameState === 'heating' ? 0.004 : 0.005, flameState === 'heating' ? 0.04 : 0.06, 16]} />
            <meshBasicMaterial 
              color={flameState === 'heating' ? '#44ccff' : '#aaccff'} 
              transparent 
              opacity={flameState === 'heating' ? 0.8 : 0.9} 
              blending={THREE.AdditiveBlending}
              depthWrite={false}
            />
          </mesh>

          {/* Outer Cone ( secondary envelope ) */}
          <mesh ref={outerConeRef} position={[0, flameState === 'heating' ? 0.04 : 0.06, 0]}>
            {/* The heating flame roars into a tight outer cone; safety flame produces a taller wavy envelope */}
            <coneGeometry args={[
              flameState === 'heating' ? 0.006 : 0.008, 
              flameState === 'heating' ? 0.09 : 0.12, 
              24, 
              1, 
              true // open ended at bottom looks slightly nicer blending
            ]} />
            <meshBasicMaterial 
              color={flameState === 'heating' ? '#0033ff' : '#ffa500'} 
              transparent 
              opacity={flameState === 'heating' ? 0.45 : 0.6} 
              blending={THREE.AdditiveBlending}
              depthWrite={false}
              side={THREE.DoubleSide}
            />
          </mesh>

          {/* Ambient Flame Outer Glow - gives that beautiful fuzzy aurora */}
          <mesh position={[0, 0.04, 0]}>
            <sphereGeometry args={[0.015, 16, 16]} />
            <meshBasicMaterial 
              color={flameState === 'heating' ? '#0022cc' : '#ff7700'} 
              transparent 
              opacity={0.15} 
              blending={THREE.AdditiveBlending}
              depthWrite={false}
            />
          </mesh>

          {/* Ambient light for the flame casting onto the environment */}
          <pointLight 
            ref={lightRef}
            position={[0, 0.05, 0]} 
            color={flameState === 'heating' ? '#baddff' : '#ffdd88'} 
            distance={0.8} 
            decay={2}
            intensity={0} // Managed by useFrame 
          />
        </group>
      )}
    </group>
  );
}